/* eslint-disable complexity */
import { useEffect, useState } from "react";

import { css, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import type { Token } from "@/libraries/design/foundations/token/src/token-types";
import {
    ProductKeyTypes,
    TypographyStyles
} from "@/libraries/wpl/foundations/src";
import { Cross, DataEventProps } from "@/libraries/wpl/foundations/src";
import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

export interface EnhancedBannerProps {
    heading: string;
    subheading?: string;
    subheadingMobile?: string;
    ctaLinkHref: string;
    ctaLinkText: string;
    logoSrc: string;
    logoAlt: string;
    backgroundImageDesktop?: string;
    backgroundImageTablet?: string;
    backgroundImageMobile?: string;
    productKey?: ProductKeyTypes;
    uniqueIdentifier?: string;
    backgroundColor?: "lightBlue" | "black" | "primaryBlue";
    shapeColor1?: string;
    shapeColor2?: string;
    shapeColor3?: string;
}

const scrollUpTwoScreens = keyframes({
    "0%": {
        opacity: "1",
        transform: "translateY(50%)"
    },
    "2%": {
        transform: "translateY(0)"
    },
    "49%": {
        opacity: "1"
    },
    "50%": {
        opacity: "0"
    },
    "100%": {
        opacity: "0"
    }
});

const containerStyles = css({
    "position": "relative",
    "overflow": "hidden",
    "margin": 0,
    "display": "flex",
    "zIndex": 1,
    "transition": "padding-inline-start 0.3s ease-in-out",
    "paddingInlineStart": "272px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`.design-tablet, .design-mobile`]: {
        display: "none"
    },
    "@media (min-width: 1024px) and (max-width: 1439px)": {
        paddingInlineStart: "232px",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.design-desktop`]: {
            insetInlineStart: "-40px"
        }
    },
    "@media (min-width: 768px) and (max-width: 1023.99px)": {
        paddingInlineStart: "152px",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.design-desktop`]: {
            display: "none"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.design-tablet`]: {
            display: "block"
        }
    },
    "@media (max-width: 767.99px)": {
        paddingInlineStart: "90px",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.design-desktop, .design-tablet`]: {
            display: "none"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.design-mobile`]: {
            display: "block"
        }
    },
    [`&[data-dismissed="true"]`]: {
        display: "none"
    }
});

const enhancedBannerRowStyles = css({
    flexGrow: 1,
    display: "flex",
    width: "100%",
    flexDirection: "row",
    gap: token("space.400"),
    paddingInlineEnd: "80px",
    position: "relative"
});

const desktopContainerStyles = css({
    "paddingBlock": token("space.200"),
    "display": "flex",
    "flexDirection": "row",
    "gap": token("space.400"),
    "width": "100%",
    "position": "relative",
    "marginBlockEnd": "-2px",
    "@media (max-width: 768px)": {
        display: "none"
    }
});

const mobileContainerStyles = css({
    "paddingBlock": token("space.200"),
    "display": "none",
    "width": "100%",
    "flexDirection": "column",
    "alignItems": "center",
    "gap": token("space.200"),
    "@media (max-width: 768px)": {
        display: "flex"
    }
});

const leftDesign = css({
    position: "absolute",
    insetBlock: 0,
    insetInlineStart: 0,
    insetInlineEnd: 0,
    transition: "inset-inline-start 0.3s ease-in-out",
    height: "100%"
});

const leftDesignDesktopStyles = css({
    width: "240px"
});

const leftDesignTabletStyles = css({
    width: "120px"
});

const leftDesignMobileStyles = css({
    width: "64px"
});

const bgTileStyles = css({
    width: "100%",
    height: "100%",
    backgroundSize: "cover",
    backgroundPosition: "right center",
    backgroundRepeat: "no-repeat"
});

const desktopLogoStyles = css({
    display: "flex",
    alignItems: "center"
});

const marqueeContainerStyles = css({
    display: "grid",
    maxWidth: "100%",
    paddingInline: token("space.200")
});

const marqueeItemStyle = css({
    gridRow: 1,
    gridColumn: 1,
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
    animationName: `${scrollUpTwoScreens}`,
    animationDuration: "6s"
});

const marqueeItem1Styles = css({
    margin: "0 auto"
});

const marqueeItem2Styles = css({
    textAlign: "center",
    marginBlock: "auto",
    opacity: 0,
    animationDelay: "3s"
});

const contentStyles = css({
    "width": "100%",
    "display": "flex",
    "flexGrow": 1,
    "flexDirection": "row",
    "justifyContent": "space-between",
    "alignItems": "center",
    "gap": token("space.400"),
    "@media (max-width: 1024px)": {
        flexDirection: "column",
        alignItems: "flex-start",
        gap: token("space.150")
    },
    "@media (max-width: 768px)": {
        alignItems: "center"
    }
});

const textColorStyles = css({
    [`&[data-color="lightBlue"]`]: {
        color: wacToken("color.primary.black")
    },
    [`&[data-color="black"], &[data-color="primaryBlue"]`]: {
        color: wacToken("color.neutral.light0")
    }
});

const headingStyle = css({
    margin: "0",
    fontWeight: token("font.weight.bold"),
    zIndex: 1
});

const subheadingStyle = css({
    margin: `${token("space.050")} 0 0 0`,
    zIndex: 1
});

const lineStyle = css({
    "borderInlineStartWidth": `1px`,
    "borderInlineStartStyle": `solid`,
    "height": "100%",

    "@media (max-width: 768px)": {
        display: "none"
    }
});

const closeStyles = css({
    border: 0,
    appearance: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    position: "absolute",
    zIndex: 1,
    insetBlockStart: 0,
    insetInlineEnd: 0,
    margin: token("space.200"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [`svg path`]: {
        fill: "var(--enhanced-banner-text-color)"
    }
});

export const EnhancedBanner = (props: EnhancedBannerProps) => {
    const identifier =
        props.uniqueIdentifier ||
        props.heading.replace(/\s+/g, "-").toLowerCase();
    const bannerStorageItem = `BannerAlert_dismiss-${identifier}`;
    const productKey = props.productKey || "home";

    const backgroundColor = props.backgroundColor || "lightBlue";
    const bgColorToken =
        backgroundColor === "lightBlue"
            ? wacToken("color.extended.blue100")
            : backgroundColor === "black"
            ? wacToken("color.primary.black")
            : wacToken("color.primary.blue");

    const textColor =
        backgroundColor === "black" || backgroundColor === "primaryBlue"
            ? wacToken("color.neutral.light0")
            : wacToken("color.primary.black");

    const getShapeColor = (colorValue?: string): string => {
        if (!colorValue) return wacToken("color.extended.blue100");
        if (colorValue.startsWith("neutral-")) {
            const tokenPath = `color.${colorValue.replace("-", ".")}` as Token;
            return wacToken(tokenPath);
        }
        if (colorValue.startsWith("secondary-")) {
            const tokenPath = `color.${colorValue.replace("-", ".")}` as Token;
            return wacToken(tokenPath);
        }
        const tokenPath = `color.extended.${colorValue}` as Token;
        return wacToken(tokenPath);
    };

    const shapeColor1 = getShapeColor(props.shapeColor1 || "saffron400");
    const shapeColor2 = getShapeColor(props.shapeColor2 || "neutral-light0");
    const shapeColor3 = getShapeColor(props.shapeColor3 || "lime500");

    const [isHidden, setIsHidden] = useState<boolean>(true);

    useEffect(() => {
        const dismissValue = getLocalStorage(bannerStorageItem);

        if (dismissValue !== "1") {
            setIsHidden(false);
        }
    }, [bannerStorageItem]);

    const closeIconDataEventProps: DataEventProps = {
        "data-product-key": productKey,
        "data-event-component": "button",
        "data-event-container": "announcementBanner",
        "data-event": "clicked",
        "data-label": "Close",
        "data-label-english": "Close"
    };

    const ctaDataEventProps: DataEventProps = {
        "data-product-key": productKey,
        "data-event-component": "linkButton",
        "data-event-container": "announcementBanner",
        "data-event": "clicked"
    };

    const intl = useIntl();

    return (
        <TypographyStyles>
            <div
                style={
                    {
                        "--enhanced-banner-text-color": textColor
                    } as React.CSSProperties
                }
                css={[
                    containerStyles,
                    css({ backgroundColor: bgColorToken, color: textColor })
                ]}
                data-dismissed={isHidden ? "true" : undefined}
                data-testid="enhanced-banner-container"
                className="banner-enhanced"
            >
                <div
                    className="left-design design-desktop"
                    css={[leftDesign, leftDesignDesktopStyles]}
                >
                    {props.backgroundImageDesktop ? (
                        <div
                            className="bg-tile"
                            css={bgTileStyles}
                            style={{
                                backgroundImage: `url(${props.backgroundImageDesktop})`
                            }}
                        />
                    ) : (
                        <svg
                            version="1.2"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 180 80"
                            width="240"
                            height="100%"
                            preserveAspectRatio="none"
                        >
                            <path
                                fill={shapeColor2}
                                d="m121.4-2.6l-22.5 74.9c38.6-29.9 51.7-40.6 81.6-75.8z"
                            />
                            <path
                                fill={shapeColor1}
                                d="m134.6 0l2.7 43.2-42.3 39.8-95-3v-80z"
                            />
                            <path
                                fill={shapeColor3}
                                d="m81.3 85.5c9.4-5.9 18.4-12.1 26.9-18.6 10.2-7.9 29.1-24.9 29.1-24.9l2.7 46.5z"
                            />
                        </svg>
                    )}
                </div>
                <div
                    className="left-design design-tablet"
                    css={[leftDesign, leftDesignTabletStyles]}
                >
                    {props.backgroundImageTablet ? (
                        <div
                            className="bg-tile"
                            css={bgTileStyles}
                            style={{
                                backgroundImage: `url(${props.backgroundImageTablet})`
                            }}
                        />
                    ) : (
                        <svg
                            version="1.2"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 120 144"
                            width="120"
                            height="100%"
                            preserveAspectRatio="none"
                        >
                            <path
                                fill={shapeColor2}
                                d="m63.3 149c23.7-54 44.3-120.4 56.7-154.6l-123.4 2 0.3 147.6z"
                            />
                            <path
                                fill={shapeColor1}
                                d="m88.7-7.7l4.7 144.7-33.4 12-63.2-1.1 3.2-147.9z"
                            />
                            <path
                                fill={shapeColor3}
                                d="m57.7 147.8q7.5-13.1 14.4-27.4 10.3-21.4 19.2-45.2l3.7 72.8z"
                            />
                        </svg>
                    )}
                </div>
                <div
                    className="left-design design-mobile"
                    css={[leftDesign, leftDesignMobileStyles]}
                >
                    {props.backgroundImageMobile ? (
                        <div
                            className="bg-tile"
                            css={bgTileStyles}
                            style={{
                                backgroundImage: `url(${props.backgroundImageMobile})`
                            }}
                        />
                    ) : (
                        <svg
                            version="1.2"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 64 108"
                            width="64"
                            height="100%"
                            preserveAspectRatio="none"
                        >
                            <path
                                fill={shapeColor2}
                                d="m-6.2 108h38.2c13.2-39.6 22.8-67.5 33.1-114l-74 0.8z"
                            />
                            <path
                                fill={shapeColor1}
                                d="m49.2 111.9l-1.2-115.9h-48v112z"
                            />
                            <path
                                fill={shapeColor3}
                                d="m29.9 113q4.5-10.8 8.6-22.7 5.4-16 10.2-33.9l3.3 55.6z"
                            />
                        </svg>
                    )}
                </div>
                <div
                    className="enhanced-banner-row"
                    css={enhancedBannerRowStyles}
                >
                    <>
                        <div
                            css={desktopContainerStyles}
                            className="desktop-container"
                        >
                            <div css={desktopLogoStyles}>
                                <Image
                                    src={props.logoSrc}
                                    alt={props.logoAlt}
                                    style={{
                                        maxWidth: "200px",
                                        height: "40px",
                                        width: "auto"
                                    }}
                                />
                            </div>
                            <div css={lineStyle} className="line" />
                            <div css={contentStyles}>
                                <div>
                                    <p
                                        css={[headingStyle, textColorStyles]}
                                        data-color={backgroundColor}
                                    >
                                        <strong id="heading-desktop">
                                            {props.heading}
                                        </strong>
                                    </p>
                                    {props.subheading && (
                                        <p
                                            css={[
                                                subheadingStyle,
                                                textColorStyles
                                            ]}
                                            data-color={backgroundColor}
                                        >
                                            {props.subheading}
                                        </p>
                                    )}
                                </div>
                                <Link
                                    ariaLabelledby="heading-desktop"
                                    aria-label={props.ctaLinkText}
                                    bgColor={
                                        backgroundColor === "black" ||
                                        backgroundColor === "primaryBlue"
                                            ? "bgBlackBlue"
                                            : "bgWhite"
                                    }
                                    text={props.ctaLinkText}
                                    href={props.ctaLinkHref}
                                    type="primary"
                                    dataEventProps={
                                        {
                                            ...ctaDataEventProps
                                        } as DataEventProps
                                    }
                                />
                            </div>
                        </div>

                        <div
                            css={mobileContainerStyles}
                            className="mobile-container"
                        >
                            <div css={marqueeContainerStyles}>
                                <div
                                    css={[marqueeItemStyle, marqueeItem1Styles]}
                                >
                                    <div>
                                        <Image
                                            src={props.logoSrc}
                                            alt={props.logoAlt}
                                            style={{
                                                maxWidth: "200px",
                                                height: "40px",
                                                width: "auto"
                                            }}
                                        />
                                    </div>
                                </div>
                                <div
                                    css={[marqueeItemStyle, marqueeItem2Styles]}
                                >
                                    <p
                                        css={[headingStyle, textColorStyles]}
                                        data-color={backgroundColor}
                                    >
                                        <strong id="heading-mobile">
                                            {props.heading}
                                        </strong>
                                    </p>
                                    {(props.subheadingMobile ||
                                        props.subheading) && (
                                        <p
                                            css={[
                                                subheadingStyle,
                                                textColorStyles
                                            ]}
                                            data-color={backgroundColor}
                                        >
                                            {props.subheadingMobile ||
                                                props.subheading}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <Link
                                ariaLabelledby="heading-mobile"
                                aria-label={props.ctaLinkText}
                                bgColor={
                                    backgroundColor === "black" ||
                                    backgroundColor === "primaryBlue"
                                        ? "bgBlackBlue"
                                        : "bgWhite"
                                }
                                text={props.ctaLinkText}
                                href={props.ctaLinkHref}
                                type="primary"
                                dataEventProps={
                                    {
                                        ...ctaDataEventProps
                                    } as DataEventProps
                                }
                            />
                        </div>
                    </>
                </div>

                <button
                    className="close-button-symbol"
                    data-testid="close-button"
                    aria-label={intl.formatMessage({
                        id: "uniform.enhanced-banner.close.aria-label",
                        defaultMessage: "Close"
                    })}
                    style={{ color: textColor }}
                    css={closeStyles}
                    onClick={() => {
                        setIsHidden(true);
                        setLocalStorage(bannerStorageItem, "1");
                    }}
                    {...closeIconDataEventProps}
                >
                    <Cross size="24" />
                </button>
            </div>
        </TypographyStyles>
    );
};
